<template>
  <div class="landing-page">
    <!-- Navbar -->
    <nav class="navbar">
      <div class="nav-container">
        <div class="logo">
          <img src="@/assets/amana1.jpeg" alt="Inveep GPAO Logo" class="logo-img" />
          <span class="logo-text">Inveep GPAO</span>
        </div>
        <div class="nav-links">
          <a href="#features">Fonctionnalités</a>
          <a href="#targets">Nos Cibles</a>
          <a href="#pricing">Tarification</a>
          <router-link v-if="isAuthenticated" to="/dashboard" class="btn btn-outline">Espace Travail</router-link>
          <template v-else>
            <router-link to="/login" class="btn btn-link">Connexion</router-link>
            <router-link to="/signup" class="btn btn-primary">Essai Gratuit</router-link>
          </template>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <header class="hero">
      <div class="hero-container">
        <div class="hero-content">
          <span class="badge">SaaS Multi-tenant Pro</span>
          <h1>Optimisez votre <span>GPAO</span> & vos Plannings Industriels</h1>
          <p>La plateforme tout-en-un de gestion de production, planification d'interventions, suivi de stock et facturation pour booster la productivité de vos équipes sur le terrain.</p>
          <div class="hero-actions">
            <router-link to="/signup" class="btn btn-primary btn-lg">Démarrer maintenant (30 jours gratuits)</router-link>
            <a href="#features" class="btn btn-secondary btn-lg">Découvrir les fonctionnalités</a>
          </div>
        </div>
        <div class="hero-image">
          <div class="image-wrapper">
            <!-- Glassmorphism dashboard preview mockup -->
            <div class="dashboard-mockup">
              <div class="mockup-header">
                <span class="dot red"></span>
                <span class="dot yellow"></span>
                <span class="dot green"></span>
                <span class="mockup-title">Inveep GPAO Dashboard</span>
              </div>
              <div class="mockup-body">
                <div class="mockup-sidebar">
                  <div class="line short"></div>
                  <div class="line"></div>
                  <div class="line"></div>
                  <div class="line"></div>
                </div>
                <div class="mockup-content">
                  <div class="mockup-widgets">
                    <div class="widget card-1">
                      <i class="fas fa-tasks text-green"></i>
                      <h4>98.2%</h4>
                      <p>Taux de complétion</p>
                    </div>
                    <div class="widget card-2">
                      <i class="fas fa-boxes text-green"></i>
                      <h4>1,420</h4>
                      <p>Articles en Stock</p>
                    </div>
                  </div>
                  <div class="mockup-chart">
                    <div class="chart-bar" style="height: 60%"></div>
                    <div class="chart-bar" style="height: 80%"></div>
                    <div class="chart-bar" style="height: 45%"></div>
                    <div class="chart-bar" style="height: 95%"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Features Section -->
    <section id="features" class="features">
      <div class="section-header">
        <h2>Une suite complète de fonctionnalités</h2>
        <p>Tout ce dont votre entreprise a besoin pour gérer la planification et le stock dans une seule interface.</p>
      </div>
      <div class="features-grid">
        <div class="feature-card">
          <div class="icon-box"><i class="fas fa-calendar-check"></i></div>
          <h3>Planning &amp; Affectations</h3>
          <p>Planifiez les interventions de vos techniciens en temps réel avec des fiches de tâches complètes et des suivis géolocalisés.</p>
        </div>
        <div class="feature-card">
          <div class="icon-box"><i class="fas fa-boxes"></i></div>
          <h3>Gestion de Stock Moderne</h3>
          <p>Suivez les entrées, sorties et transferts d'équipements avec des alertes automatiques en cas de rupture de seuil.</p>
        </div>
        <div class="feature-card">
          <div class="icon-box"><i class="fas fa-file-invoice-dollar"></i></div>
          <h3>Facturation &amp; Caisse</h3>
          <p>Gérez les encaissements, éditez des bons de livraison/retour et suivez les créances clients en temps réel.</p>
        </div>
        <div class="feature-card">
          <div class="icon-box"><i class="fas fa-chart-line"></i></div>
          <h3>Analyses &amp; Statistiques</h3>
          <p>Mesurez la performance de vos équipes sur le terrain à l'aide de rapports visuels clairs et exportables.</p>
        </div>
      </div>
    </section>

    <!-- Targets Section -->
    <section id="targets" class="targets">
      <div class="section-header">
        <h2>Conçu pour vos secteurs d'activité</h2>
        <p>Inveep GPAO s'adapte à vos problématiques métier et aux besoins de vos collaborateurs.</p>
      </div>
      <div class="targets-grid">
        <div class="target-card">
          <div class="target-img"><i class="fas fa-industry"></i></div>
          <h3>Entreprises de Production</h3>
          <p>Gérez l'approvisionnement des lignes de montage, l'affectation des tâches de maintenance et le suivi des ordres de travail.</p>
        </div>
        <div class="target-card">
          <div class="target-img"><i class="fas fa-shipping-fast"></i></div>
          <h3>Logistique &amp; Transport</h3>
          <p>Assurez la liaison entre l'entrepôt principal de stockage et les véhicules de livraison grâce au suivi de colis en temps réel.</p>
        </div>
        <div class="target-card">
          <div class="target-img"><i class="fas fa-hard-hat"></i></div>
          <h3>Services sur le Terrain</h3>
          <p>Parfait pour les installateurs de GPS, techniciens de maintenance réseau ou professionnels du BTP en déplacement.</p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-container">
        <h2>Prêt à optimiser vos opérations industrielles ?</h2>
        <p>Rejoignez les entreprises qui font confiance à Inveep GPAO pour simplifier leur quotidien.</p>
        <router-link to="/signup" class="btn btn-primary btn-lg">Créer mon espace d'entreprise gratuitement</router-link>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-container">
        <p>&copy; 2026 Inveep GPAO. Tous droits réservés.</p>
        <div class="footer-links">
          <router-link to="/login">Espace Membre</router-link>
          <a href="#">Conditions d'utilisation</a>
          <a href="#">Politique de confidentialité</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'Home',
  setup() {
    const store = useStore()
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])

    return {
      isAuthenticated
    }
  }
}
</script>

<style scoped>
.landing-page {
  font-family: 'Outfit', 'Inter', sans-serif;
  color: #1e293b;
  background-color: #fafbfd;
  line-height: 1.6;
}

/* Navbar */
.navbar {
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #e2e8f0;
  z-index: 100;
  padding: 15px 0;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-img {
  width: 42px;
  height: 42px;
  border-radius: 8px;
}

.logo-text {
  font-size: 20px;
  font-weight: 700;
  color: #10b981; /* Green color close to logoinveep */
  letter-spacing: -0.5px;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-links a {
  text-decoration: none;
  color: #475569;
  font-weight: 500;
  font-size: 14px;
  transition: color 0.3s;
}

.nav-links a:hover {
  color: #10b981;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-block;
}

.btn-primary {
  background-color: #10b981;
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.btn-secondary {
  background-color: #e2e8f0;
  color: #334155;
  border: none;
}

.btn-secondary:hover {
  background-color: #cbd5e1;
}

.btn-outline {
  border: 1px solid #10b981;
  color: #10b981;
  background: transparent;
}

.btn-outline:hover {
  background: #ecfdf5;
}

.btn-link {
  color: #10b981;
}

.btn-lg {
  padding: 14px 28px;
  font-size: 16px;
  border-radius: 8px;
}

/* Hero Section */
.hero {
  padding: 80px 20px;
  background: radial-gradient(circle at 10% 20%, rgba(236, 253, 245, 0.7) 0%, rgba(250, 251, 253, 1) 90%);
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: center;
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.badge {
  background: #d1fae5;
  color: #065f46;
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 20px;
}

.hero-content h1 {
  font-size: 46px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -1px;
  margin-bottom: 20px;
  color: #0f172a;
}

.hero-content h1 span {
  color: #10b981;
}

.hero-content p {
  font-size: 17px;
  color: #475569;
  margin-bottom: 35px;
}

.hero-actions {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.hero-image {
  display: flex;
  justify-content: center;
}

.image-wrapper {
  position: relative;
  width: 100%;
  max-width: 500px;
}

.dashboard-mockup {
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.mockup-header {
  background: #f8fafc;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.red { background: #ef4444; }
.yellow { background: #f59e0b; }
.green { background: #10b981; }

.mockup-title {
  margin-left: auto;
  margin-right: auto;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
}

.mockup-body {
  display: flex;
  height: 250px;
}

.mockup-sidebar {
  width: 80px;
  border-right: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.line {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  width: 100%;
}

.line.short {
  width: 60%;
  background: #cbd5e1;
}

.mockup-content {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mockup-widgets {
  display: flex;
  gap: 15px;
}

.widget {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}

.widget i {
  font-size: 18px;
  margin-bottom: 6px;
  display: inline-block;
}

.text-green {
  color: #10b981;
}

.widget h4 {
  margin: 0;
  font-size: 16px;
}

.widget p {
  margin: 2px 0 0 0;
  font-size: 9px;
  color: #64748b;
}

.mockup-chart {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
}

.chart-bar {
  width: 25px;
  background: #10b981;
  border-radius: 4px 4px 0 0;
  opacity: 0.85;
}

/* Features Section */
.features, .targets {
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  max-width: 700px;
  margin: 0 auto 50px auto;
}

.section-header h2 {
  font-size: 32px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 12px;
}

.section-header p {
  color: #64748b;
  font-size: 16px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.feature-card {
  background: white;
  padding: 30px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  border-color: #10b981;
}

.icon-box {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #ecfdf5;
  color: #10b981;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.feature-card p {
  color: #64748b;
  font-size: 14px;
}

/* Targets Section */
.targets {
  border-top: 1px solid #e2e8f0;
}

.targets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.target-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 35px;
  text-align: center;
  transition: all 0.3s;
}

.target-card:hover {
  transform: translateY(-5px);
  border-color: #10b981;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
}

.target-img {
  font-size: 36px;
  color: #10b981;
  margin-bottom: 20px;
}

.target-card h3 {
  font-size: 19px;
  font-weight: 600;
  margin-bottom: 12px;
}

.target-card p {
  color: #64748b;
  font-size: 14px;
}

/* CTA Section */
.cta-section {
  background: #10b981;
  padding: 80px 20px;
  text-align: center;
  color: white;
}

.cta-container {
  max-width: 800px;
  margin: 0 auto;
}

.cta-section h2 {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 15px;
}

.cta-section p {
  font-size: 18px;
  margin-bottom: 35px;
  opacity: 0.9;
}

.cta-section .btn-primary {
  background: white;
  color: #10b981;
}

.cta-section .btn-primary:hover {
  background: #f8fafc;
  color: #059669;
}

/* Footer */
.footer {
  background: #0f172a;
  color: #94a3b8;
  padding: 40px 20px;
  border-top: 1px solid #334155;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.footer p {
  margin: 0;
  font-size: 14px;
}

.footer-links {
  display: flex;
  gap: 20px;
}

.footer-links a {
  color: #94a3b8;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: white;
}

@media (max-width: 768px) {
  .hero-container {
    grid-template-columns: 1fr;
  }
  .navbar .nav-links {
    display: none; /* simple responsive toggle */
  }
  .hero-content h1 {
    font-size: 32px;
  }
}
</style>
